/**
 * Library Resource Card Component
 * Displays a library resource with type indicator, topics, difficulty, bookmark, and progress
 */

import { Link } from "react-router-dom";
import { cn } from "@utils/cn";
import {
  getDifficultyStyles,
  formatTopic,
  formatResourceType,
} from "@utils/icons";
import type {
  LibraryResource,
  LibraryResourceWithUserData,
} from "@/types/models";
import { BookmarkButton } from "./BookmarkButton";
import { ProgressIcon } from "./ProgressIndicator";

interface LibraryResourceCardProps {
  resource: LibraryResource | LibraryResourceWithUserData;
  /** If true, shows bookmark button and progress indicator (requires resource to have user data) */
  showUserData?: boolean;
}

// External link icon
function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}

// Type guard to check if resource has user data
function hasUserData(
  resource: LibraryResource | LibraryResourceWithUserData,
): resource is LibraryResourceWithUserData {
  return "isBookmarked" in resource;
}

export function LibraryResourceCard({
  resource,
  showUserData = false,
}: LibraryResourceCardProps) {
  const isExternal = resource.source === "external";
  const resourceWithUserData = hasUserData(resource) ? resource : null;

  const cardContent = (
    <div className="p-6">
      {/* Badges row: Content Type on left, Difficulty/External on right */}
      <div className="flex items-center justify-between mb-4">
        <span className="inline-block px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
          {formatResourceType(resource.type)}
        </span>
        <div className="flex items-center gap-2">
          {/* Progress indicator */}
          {showUserData && resourceWithUserData && (
            <ProgressIcon status={resourceWithUserData.progressStatus} />
          )}
          {isExternal && (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
              External
              <ExternalLinkIcon className="w-3 h-3" />
            </span>
          )}
          <span
            className={cn(
              "px-2 py-1 text-xs font-medium rounded-full capitalize",
              getDifficultyStyles(resource.difficulty),
            )}
          >
            {resource.difficulty}
          </span>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2">
        {resource.title}
      </h3>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
        {resource.description}
      </p>

      {/* Topics */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {resource.topics.slice(0, 2).map((topic) => (
          <span
            key={topic}
            className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
          >
            {formatTopic(topic)}
          </span>
        ))}
        {resource.topics.length > 2 && (
          <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
            +{resource.topics.length - 2}
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-2">
          {resource.author && <span>{resource.author}</span>}
          {resource.estimatedMinutes && (
            <span className="flex items-center gap-1">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {resource.estimatedMinutes} min
            </span>
          )}
        </div>
        {/* Bookmark button */}
        {showUserData && resourceWithUserData && (
          <BookmarkButton
            resourceId={resource.id}
            isBookmarked={resourceWithUserData.isBookmarked}
            size="sm"
          />
        )}
      </div>
    </div>
  );

  // External resources open in new tab
  if (isExternal && resource.externalUrl) {
    return (
      <a
        href={resource.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-700 transition-all duration-200"
      >
        {cardContent}
      </a>
    );
  }

  // Embedded resources link to detail page
  return (
    <Link
      to={`/library/${resource.id}`}
      className="group block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-700 transition-all duration-200"
    >
      {cardContent}
    </Link>
  );
}
